import { createFileRoute, Link } from "@tanstack/react-router";

import { PageHero, Prose, ProseSection } from "@/components/page-hero";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Quibblies" },
      {
        name: "description",
        content:
          "What Quibblies collects, why we hold date of birth and moderation records, what stays private by default, and how to export or delete your data.",
      },
      { property: "og:title", content: "Privacy Policy — Quibblies" },
      {
        property: "og:description",
        content: "How Quibblies handles member data, moderation records, retention and exports.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div>
      <PageHero
        eyebrow="Privacy"
        title="Privacy policy"
        description="Quibblies is a privacy-sensitive fiction community. We collect as little as we can, and we are explicit about what is public."
      />
      <Prose>
        <ProseSection title="What we collect">
          <ul className="list-disc space-y-1 pl-5">
            <li>Account data: email, password hash, date of birth, agreement timestamps.</li>
            <li>Profile data: username, display name or pen names, bio, links, images.</li>
            <li>Content: stories, chapters, versions, comments and uploads.</li>
            <li>Activity: likes, follows, bookmarks and reading progress.</li>
            <li>Safety data: reports, moderation decisions, appeals and audit records.</li>
          </ul>
        </ProseSection>
        <ProseSection title="Private by default">
          <p>
            Bookmarks and reading history are private by default. You control whether your follower
            list, liked stories and reading activity are visible to others in privacy settings.
          </p>
        </ProseSection>
        <ProseSection title="Pen names">
          <p>
            Publishing under a pen name hides the connection to your account from other members.
            Staff can still link a pen name to an account for safety and legal purposes.
          </p>
        </ProseSection>
        <ProseSection title="Date of birth">
          <p>
            Quibblies is strictly 18+. Date of birth is stored to evidence that check and is never
            shown on your profile.
          </p>
        </ProseSection>
        <ProseSection title="Emails you receive">
          <p>
            Transactional emails — verification, account decisions, password resets, story and
            image decisions, and account actions — are always sent. Social and marketing emails are
            controlled by your notification preferences.
          </p>
        </ProseSection>
        <ProseSection title="Deletion and deactivation">
          <p>
            Deactivating hides your profile and writing while keeping everything recoverable.
            Deleting removes your profile and unpublished drafts; moderation records, reports and
            financial records are retained in anonymised form where we are required to keep them.
          </p>
        </ProseSection>
        <ProseSection title="Export">
          <p>
            You can download your writing and account data at any time from account settings.
          </p>
        </ProseSection>
        <ProseSection title="Questions">
          <p>
            Privacy requests go through the{" "}
            <Link to="/contact" className="text-primary-glow underline-offset-4 hover:underline">
              contact form
            </Link>
            . See also the{" "}
            <Link to="/terms" className="text-primary-glow underline-offset-4 hover:underline">
              terms
            </Link>{" "}
            and{" "}
            <Link
              to="/guidelines"
              className="text-primary-glow underline-offset-4 hover:underline"
            >
              community guidelines
            </Link>
            .
          </p>
        </ProseSection>
      </Prose>
    </div>
  );
}
